import React from 'react';

import { parseDateValue } from 'components/entityViewer/entitiesDropDownFilter/entityFilterControls/DateRangeFilter';
import { ClockIcon } from 'components/opsVisibility/components/inventoryHistoryV2/filterComponentTailwind/Icons';
import { Button } from 'shadcn/components/ui/Button';
import { DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { createPastDaysRange } from 'utils/dateUtils/ledgerDateUtils';

const QUICK_SELECT_PRESETS = [
    { days: 1, label: '1D', title: 'Past 1 Day' },
    { days: 2, label: '2D', title: 'Past 2 Days' },
    { days: 3, label: '3D', title: 'Past 3 Days' },
    { days: 7, label: '7D', title: 'Past 7 Days' },
];

function QuickSelectButton({ onClick, timeText, tooltip }) {
    return (
        <Button
            type="button"
            size="sm"
            variant="outline"
            title={tooltip}
            onClick={onClick}
            className="relative h-auto px-3 py-1.5 [&_svg]:size-5"
        >
            <span className="flex items-center justify-center">
                <ClockIcon />
                <span className="absolute bottom-0 right-0 text-[0.6rem] px-0.5 py-0 bg-background rounded font-bold leading-none">
                    {timeText}
                </span>
            </span>
        </Button>
    );
}

function toInputValue(dateValue) {
    const date = parseDateValue(dateValue);
    if (!date) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function getPickedFromInput(raw) {
    if (!raw) return undefined;
    const date = parseDateValue(raw);
    return date && !Number.isNaN(date.getTime()) ? date : undefined;
}

/**
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.label
 * @param {(nextValue: Object | undefined) => void} props.onChange
 * @param {{ mode?: 'on' | 'range', date?: Date | string, startDate?: Date | string, endDate?: Date | string } | undefined} props.value
 * @returns {JSX.Element}
 */
export function CreatedDateFilterMenuItem({ id, label, onChange, value }) {
    const mode = value?.mode === 'on' ? 'on' : 'range';
    const singleDateObj = value?.date ? parseDateValue(value.date) : undefined;
    const startDateObj = value?.startDate ? parseDateValue(value.startDate) : undefined;
    const endDateObj = value?.endDate ? parseDateValue(value.endDate) : undefined;

    const setMode = (nextMode) => {
        if (nextMode === 'on') {
            onChange({ mode: 'on', date: undefined });
            return;
        }
        onChange({ mode: 'range', startDate: undefined, endDate: undefined });
    };

    const applyPastDaysRange = (days) => {
        const { endDate, startDate } = createPastDaysRange(days);
        onChange({ mode: 'range', startDate, endDate });
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="p-0">
                <div className="w-[360px] px-3 py-3">
                    <div className="mb-3 flex gap-1">
                        <Button
                            type="button"
                            size="sm"
                            variant={mode === 'on' ? 'default' : 'outline'}
                            onClick={() => setMode('on')}
                        >
                            On
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={mode === 'range' ? 'default' : 'outline'}
                            onClick={() => setMode('range')}
                        >
                            Range
                        </Button>
                    </div>
                    {mode === 'on' ? (
                        <div className="flex flex-col gap-1">
                            <Label htmlFor={`${id}-on`}>Created On</Label>
                            <Input
                                id={`${id}-on`}
                                type="date"
                                value={toInputValue(singleDateObj)}
                                onChange={(e) => {
                                    const picked = getPickedFromInput(e.target.value);
                                    onChange(picked ? { mode: 'on', date: picked } : undefined);
                                }}
                                className="h-8 px-2 text-sm"
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <div className="flex gap-1">
                                {QUICK_SELECT_PRESETS.map(({ days, label: presetLabel, title }) => (
                                    <QuickSelectButton
                                        key={presetLabel}
                                        timeText={presetLabel}
                                        tooltip={title}
                                        onClick={() => applyPastDaysRange(days)}
                                    />
                                ))}
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={`${id}-start`}>Start Date</Label>
                                <Input
                                    id={`${id}-start`}
                                    type="date"
                                    value={toInputValue(startDateObj)}
                                    onChange={(e) => {
                                        const picked = getPickedFromInput(e.target.value);
                                        onChange({
                                            mode: 'range',
                                            startDate: picked,
                                            endDate: endDateObj,
                                        });
                                    }}
                                    className="h-8 px-2 text-sm w-full"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor={`${id}-end`}>End Date</Label>
                                <Input
                                    id={`${id}-end`}
                                    type="date"
                                    value={toInputValue(endDateObj)}
                                    onChange={(e) => {
                                        const picked = getPickedFromInput(e.target.value);
                                        onChange({
                                            mode: 'range',
                                            startDate: startDateObj,
                                            endDate: picked,
                                        });
                                    }}
                                    className="h-8 px-2 text-sm w-full"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

CreatedDateFilterMenuItem.displayName = 'CreatedDateFilterMenuItem';
